
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertCircle, Link, User, Hash } from "lucide-react";
import { DraggableRecordCard } from './DraggableRecordCard';
import { DropZone } from './DropZone';

interface PotentialDuplicate {
  record1_id: string;
  record1_creator: string;
  record1_handler: string;
  record1_type: string;
  record2_id: string;
  record2_creator: string;
  record2_handler: string;
  record2_type: string;
  match_type: string;
  status: string;
}

interface PotentialDuplicatesListProps {
  duplicates: PotentialDuplicate[];
  onLinkRecords: (parentId: string, childId: string) => void;
  isPending: (id: string) => boolean;
  isProcessing: boolean;
}

export const PotentialDuplicatesList: React.FC<PotentialDuplicatesListProps> = ({
  duplicates,
  onLinkRecords,
  isPending,
  isProcessing
}) => {
  if (duplicates.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No Potential Duplicates Found
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            All KOL records appear to be unique or already linked in parent-child relationships.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Group duplicates by match type
  const groupedDuplicates = duplicates.reduce((acc, duplicate) => {
    const key = duplicate.match_type;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(duplicate);
    return acc;
  }, {} as Record<string, PotentialDuplicate[]>);

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-100">
              Drag & Drop Instructions
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Drag one record onto another to create a parent-child relationship. The record you drag becomes the child, 
              and the record you drop onto becomes the parent. You can also use the "Link as Parent/Child" buttons for manual linking.
            </p>
          </div>
        </div>
      </div>

      {Object.entries(groupedDuplicates).map(([matchType, groupDuplicates]) => (
        <Card key={matchType}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {matchType === 'creator_name' ? <User className="h-5 w-5" /> : <Hash className="h-5 w-5" />}
              Matches by {matchType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              <Badge variant="secondary">{groupDuplicates.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {groupDuplicates.map((duplicate) => (
              <div key={`${duplicate.record1_id}-${duplicate.record2_id}`} className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Record 1 */}
                  <DropZone
                    onDrop={(draggedId) => {
                      if (draggedId !== duplicate.record1_id) {
                        onLinkRecords(duplicate.record1_id, draggedId);
                      }
                    }}
                    className="h-full"
                  >
                    <DraggableRecordCard
                      id={duplicate.record1_id}
                      creatorName={duplicate.record1_creator}
                      handler={duplicate.record1_handler}
                      type={duplicate.record1_type}
                      isPending={isPending(duplicate.record1_id)}
                      onLinkAsParent={() => onLinkRecords(duplicate.record1_id, duplicate.record2_id)}
                      onLinkAsChild={() => onLinkRecords(duplicate.record2_id, duplicate.record1_id)}
                      isProcessing={isProcessing}
                    />
                  </DropZone>

                  {/* Record 2 */}
                  <DropZone
                    onDrop={(draggedId) => {
                      if (draggedId !== duplicate.record2_id) {
                        onLinkRecords(duplicate.record2_id, draggedId);
                      }
                    }}
                    className="h-full"
                  >
                    <DraggableRecordCard
                      id={duplicate.record2_id}
                      creatorName={duplicate.record2_creator}
                      handler={duplicate.record2_handler}
                      type={duplicate.record2_type}
                      isPending={isPending(duplicate.record2_id)}
                      onLinkAsParent={() => onLinkRecords(duplicate.record2_id, duplicate.record1_id)}
                      onLinkAsChild={() => onLinkRecords(duplicate.record1_id, duplicate.record2_id)}
                      isProcessing={isProcessing}
                    />
                  </DropZone>
                </div>
                
                {/* Quick Link Actions */}
                <div className="flex justify-center gap-2 pt-2 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onLinkRecords(duplicate.record1_id, duplicate.record2_id)}
                    disabled={isProcessing || isPending(duplicate.record1_id) || isPending(duplicate.record2_id)}
                    className="text-xs"
                  >
                    <Link className="h-3 w-3 mr-1" />
                    Link: {duplicate.record1_creator.slice(0, 15)}... as Parent
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onLinkRecords(duplicate.record2_id, duplicate.record1_id)}
                    disabled={isProcessing || isPending(duplicate.record1_id) || isPending(duplicate.record2_id)}
                    className="text-xs"
                  >
                    <Link className="h-3 w-3 mr-1" />
                    Link: {duplicate.record2_creator.slice(0, 15)}... as Parent
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
